import axios from 'axios';
import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { getCookie } from '@/shared/lib/cookies';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';

/** Bid statuses used by legacy `bid/change-status/` (status 1 is «В работе» for the operator action). */
const CHANGE_STATUS = { AT_WORK: 1, CANCELLED: 4 } as const;

const AUTH_COOKIE = 'admin_auth_token';

export interface BidNamedRef {
  id?: number | string;
  name?: string;
  phone?: string | null;
  contact_person?: string | null;
  roles?: Array<{ value?: string }>;
}

export interface BidFine {
  is_request_fine?: number | boolean;
  comment?: string;
  request_time?: number;
  status_id?: { status?: number } | null;
  fine_cost?: { total_sum?: number | string } | null;
  service_balance?: Array<{ id: number | string; balance: number | string }>;
}

export interface BidOperatorOperation {
  create_time?: number;
  type_name?: string;
  operator_id?: number | string;
  operator_name?: string;
  old_cost?: number;
  new_cost?: number;
  value?: number;
  comment?: string;
}

export interface BidWorkOrderFile {
  id: number;
  name?: string;
  mime_type?: string;
  url?: string;
  thumb_200_200?: string;
  thumb_120_120?: string;
  file_name?: string;
  doc_type?: string;
}

export interface BidAdditionalWork {
  id?: number | string;
  name?: string;
  cost?: number | string;
  cost_work?: number | string;
  cost_work_discount?: number | string;
  cost_parts?: number | string;
  status_id?: number | string;
}

export interface BidCustomerAuto {
  owner_last_name?: string;
  owner_first_name?: string;
  owner_patronymic?: string;
  vin?: string;
  licence_plate?: string;
  mileage?: number | string;
}

export interface BidWorkOrder {
  status_id?: number;
  customer_auto?: BidCustomerAuto | null;
  vehicle_photos?: BidWorkOrderFile[];
  workorder_files?: BidWorkOrderFile[];
  additional_works?: BidAdditionalWork[];
  rejected_works?: BidAdditionalWork[];
  is_auto_data_updated?: number;
}

export interface BidWorkOrderHistoryItem {
  create_time?: number;
  old_data?: Record<string, unknown>;
}

export interface BidDetail extends Record<string, unknown> {
  id: number;
  status_id?: number;
  service_id?: number | null;
  service_name?: string;
  description?: string;
  comment?: string;
  answers_count?: number;
  create_time?: number;
  final_date?: number;
  period_start?: number;
  period_end?: number;
  type_connection?: number;
  work_type?: Array<{ title?: string }> | string | null;
  utm_source?: string;
  partner_name?: string;
  source?: string;
  need_evacuator?: number | boolean;
  own_spare_parts?: number | boolean;
  desired_repair_time?: number;
  desired_cost?: number | string;
  request_type?: { code?: string; title?: string } | null;
  is_closed_by_operator?: number | boolean;
  applied_discount?: { applied_discount_percent?: number; discount_name?: string } | null;
  attached_promo_code?: { code?: string } | null;
  auto?: {
    mark?: BidNamedRef | null;
    model?: BidNamedRef | null;
    generation?: { generation_name?: string } | null;
    modification?: { name?: string } | null;
  } | null;
  customer?: BidNamedRef | null;
  images?: Array<{
    id?: number;
    url?: string;
    thumb_140_140?: string;
    thumb_120_120?: string;
  }>;
  fine?: BidFine | null;
  work_order?: BidWorkOrder | null;
  work_order_history?: BidWorkOrderHistoryItem[] | null;
  financial_operation?: {
    commission?: BidOperatorOperation | null;
    operator_change_cost?: BidOperatorOperation[];
    service_change_cost?: BidOperatorOperation | null;
  } | null;
}

export interface BidServiceRef {
  id?: number | string;
  name?: string;
  phone?: string | null;
  distance?: number | string;
  price?: number | string;
  create_time?: number;
  update_time?: number;
  time_to_answer?: number;
  work_type_prices?: Array<{ title?: string }> | string | null;
}

/** Row of `bid-service-with-cost` (offers with a price). */
export interface BidOfferRow extends Record<string, unknown> {
  service?: BidServiceRef;
  cost?: { cost?: string | number } | null;
  distance?: number | string;
  time_to_answer?: number;
  /** Photos uploaded by the STO before the work order (legacy `result_doc_photos`). */
  result_doc_photos?: BidWorkOrderFile[];
}

/** Row of `bid-service-without-cost` / `services-who-see-bid`. */
export interface BidViewerRow extends Record<string, unknown> {
  service_id?: number | string;
  bid_id?: number | string;
  createdAt?: number | string;
  service?: BidServiceRef;
}

export interface BidHistoryRow extends Record<string, unknown> {
  operator_id?: number;
  operator_name?: string;
  create_time?: number;
}

export type BidServicesKind = 'withCost' | 'withoutCost' | 'whoSee';

const SERVICES_LISTS = {
  withCost: { url: '/bid/bid-service-with-cost/', key: 'answers' },
  withoutCost: { url: '/bid/bid-service-without-cost/', key: 'services' },
  whoSee: { url: '/bid/services-who-see-bid/', key: 'services' },
} as const;

/** Legacy `getHistoryActionLog(.., type = 1)`: who closed the bid. */
const HISTORY_TYPE_BID = 1;

const serializeAccountingFiles = (files: Array<Record<string, unknown>> = []): BidWorkOrderFile[] =>
  files.map((item) => ({
    id: Number(item.id),
    name: String(item.file_name ?? item.name ?? ''),
    mime_type: String(item.mime_type ?? ''),
    url: String(item.url ?? ''),
    thumb_200_200: String(item.url ?? ''),
    file_name: String(item.file_name ?? ''),
    doc_type: String(item.doc_type ?? ''),
  }));

/** Accounting / work-order documents from the site API (legacy `accounting-files-info`). */
export async function fetchAccountingFiles(bidId: number) {
  const token = getCookie(AUTH_COOKIE);
  const { data } = await axios.get<{ success?: number; data?: Array<Record<string, unknown>> }>(
    `${__CONFIG__.SITE_API_URL}/v2/service/bid/accounting-files-info/`,
    { params: { auth_token: token, bid_id: bidId } },
  );
  return serializeAccountingFiles(data.data ?? []);
}

export async function fetchBid(bidId: number) {
  const res = await postForm<{ success?: number; bid?: BidDetail }>('/bid/view/', { bid_id: bidId });
  if (!res.bid) throw new Error('Bid not found');
  try {
    const files = await fetchAccountingFiles(bidId);
    if (!files.length) return res.bid;
    return {
      ...res.bid,
      work_order: {
        ...(res.bid.work_order ?? {}),
        workorder_files: files,
      },
    };
  } catch {
    return res.bid;
  }
}

export const editBid = (fields: Record<string, string | number | boolean | undefined | null>) =>
  submitForm('/bid/edit/', fields);

export const deleteBidFile = (fileId: number) => submitForm('/bid/delete-file/', { id: fileId });

export const createBidReview = (fields: {
  bidId: number;
  customerId: number | string;
  serviceId?: number | string;
  text: string;
  locationRate: number;
  qualificationRate: number;
  appearanceRate: number;
  qualityRate: number;
  finalRate: number;
}) =>
  submitForm('/bid/review/create/', {
    bid_id: fields.bidId,
    customer_id: fields.customerId,
    service_id: fields.serviceId || undefined,
    text: fields.text,
    location_rate: fields.locationRate,
    qualification_rate: fields.qualificationRate,
    appearance_rate: fields.appearanceRate,
    repair_quality_rate: fields.qualityRate,
    final_cost_rate: fields.finalRate,
  });

export const moderateWorkOrder = (bidId: number, result: 'approved' | 'rejected') =>
  submitForm('/bid/operator/moderate-work-order/', { bid_id: bidId, result });

export const changeBidCost = (
  bidId: number,
  serviceId: number | string,
  cost: string,
  comment: string,
) =>
  submitForm('/bid/operator/change-cost/', {
    bid_id: bidId,
    service_id: serviceId,
    cost,
    comment,
  });

export const closeBid = (bidId: number) => submitForm('/bid/operator/close-bid/', { bid_id: bidId });

export const serviceReturnFine = (bidId: number, serviceId: number | string) =>
  submitForm('/bid/operator/service-return-fine/', { bid_id: bidId, service_id: serviceId });

export const rejectServiceFineReturn = (bidId: number, serviceId: number | string, comment: string) =>
  submitForm('/bid/operator/reject-request-service-fine/', {
    bid_id: bidId,
    service_id: serviceId,
    comment,
  });

export const downloadAllAccountingFiles = (bidId: number) => {
  const token = getCookie(AUTH_COOKIE);
  window.location.href = `${__CONFIG__.SITE_API_URL}/v2/service/bid/download-accounting-file-all/?auth_token=${token}&bid_id=${bidId}`;
};

export async function fetchBidServices<T extends Record<string, unknown>>(
  kind: BidServicesKind,
  bidId: number,
  page: number,
) {
  const { url, key } = SERVICES_LISTS[kind];
  const res = await postForm<Record<string, unknown>>(url, { bid_id: bidId, page });
  return { rows: getListRows<T>(res, [key]), totalPages: getTotalPages(res) };
}

/** All offers of the bid at once (legacy `bid-service-with-cost` with `limit=0`), for the autopark «attach» select. */
export async function fetchAllBidOffers(bidId: number) {
  const res = await postForm<Record<string, unknown>>('/bid/bid-service-with-cost/', {
    bid_id: bidId,
    limit: 0,
  });
  return getListRows<BidOfferRow>(res, ['answers']);
}

export async function fetchBidHistory(bidId: number) {
  const res = await postForm<Record<string, unknown>>('/user/operator/get-history-action-log/', {
    object_id: bidId,
    type: HISTORY_TYPE_BID,
    page: 1,
  });
  return getListRows<BidHistoryRow>(res, ['items']);
}

/** Moves a new bid to «В работе» for the service that already offered a price. */
export const setBidAtWork = (bidId: number, serviceId: number | string) =>
  submitForm('/bid/change-status/', {
    bid_id: bidId,
    status_id: CHANGE_STATUS.AT_WORK,
    service_id: serviceId,
  });

export const cancelBid = (bidId: number, rejectTypeId: string, rejectDescription: string) =>
  submitForm('/bid/change-status/', {
    bid_id: bidId,
    status_id: CHANGE_STATUS.CANCELLED,
    reject_type_id: rejectTypeId,
    reject_description: rejectDescription,
  });

/** Attaches (or re-attaches) the bid to a service; also used for distributing premoderation bids. */
export const setBidService = (bidId: number, serviceId: number | string) =>
  submitForm('/bid/operator/set-service/', { bid_id: bidId, service_id: serviceId });

export const extendAnswerTime = (bidId: number, serviceId: number | string) =>
  submitForm('/bid/extend-answer-time/', { bid_id: bidId, user_id: serviceId });

export interface FoundService extends Record<string, unknown> {
  id: number;
  name?: string;
  phone?: string | null;
}

const fetchServicesBy = async (filter: Record<string, string>) => {
  const res = await postForm<Record<string, unknown>>('/user/profile/list/', {
    roles: ['service'],
    ...filter,
  });
  return getListRows<FoundService>(res, ['users']);
};

/** Searches services by id (numeric query, falling back to name) or by name. */
export async function findServices(query: string) {
  const trimmed = query.trim();
  if (/^\d+$/.test(trimmed)) {
    const byId = await fetchServicesBy({ id: trimmed });
    if (byId.length) return byId;
  }
  return fetchServicesBy({ name: trimmed });
}
